import { ProtectorApi } from '@core/api';
import { useImageryFlags } from '@hooks/useImageryFlags';
import { RootState } from '@redux/reducers';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useOrganization } from '../useOrganization';
import { useReadSystemNotification } from './useReadSystemNotification';
import {
  weeklyFieldInsightsNotificationSetup,
  weeklyFieldInsightsNotificationSetupComplete,
} from './useSystemNotifications.mock';
import { useUserNotifications } from './useUserNotifications';
import { UserNotificationDTO } from './userNotifications.models';

// Important note:
// For simplification purposes, system notifications follow UserNotificationDTO structure
function useSystemNotifications() {
  const flags = useImageryFlags();
  const { baseUser } = useSelector((state: RootState) => state.User);
  const { organization } = useOrganization();
  const { mutate } = useReadSystemNotification();
  const { data: userNotifications } = useUserNotifications();
  const query = useQuery({
    queryKey: ['system-notifications', { workspace_id: organization?.workspace_id }],
    queryFn: async () => {
      const data: UserNotificationDTO[] = [];
      const fieldInsightSetupNotification = localStorage.getItem(
        `${weeklyFieldInsightsNotificationSetup.id}-${organization?.workspace_id}`
      );
      const fieldInsightSetupCompleteNotification = localStorage.getItem(
        `${weeklyFieldInsightsNotificationSetupComplete.id}-${organization?.workspace_id}`
      );

      const { data: userSubscriptions } = await ProtectorApi.post('/subscriptions/filtered', {
        workspace_id: organization?.workspace_id,
        subscribers: [baseUser?.id],
      });

      const hasWeeklyFieldInsightsSubscription = userSubscriptions.content.some(
        (subscription) => subscription.type === 'MULTI_FIELDS_REPORT'
      );

      const hasWeeklyFieldInsightsNotification = userNotifications
        ? Object.values(userNotifications).some(
            (notification) => notification.user_notification.content.title === 'report_title'
          )
        : false;

      if (!hasWeeklyFieldInsightsSubscription && fieldInsightSetupNotification !== 'read') {
        data.push(weeklyFieldInsightsNotificationSetup);
      }

      if (
        hasWeeklyFieldInsightsSubscription &&
        !hasWeeklyFieldInsightsNotification &&
        fieldInsightSetupCompleteNotification !== 'read'
      ) {
        data.push(weeklyFieldInsightsNotificationSetupComplete);
      }

      return data.reduce<{ [key: string]: UserNotificationDTO }>((acc, cur) => {
        acc[cur.id] = cur;

        return acc;
      }, {});
    },
    enabled:
      flags.FlagShowSystemNotifications && !!organization && !!baseUser && !!userNotifications,
  });

  return { ...query, readNotifications: mutate };
}

export { useSystemNotifications };
